import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, logUrl: String, contactId: Number }
  static targets = ["list", "modal", "submitBtn"]

  connect() {
    this.loadEmails()
  }

  async loadEmails() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) {
        this.listTarget.innerHTML = '<p class="text-xs text-[var(--color-mc-text-secondary)]">Could not load email activity.</p>'
        return
      }

      const messages = await response.json()

      if (messages.length === 0) {
        this.listTarget.innerHTML = '<p class="text-xs text-[var(--color-mc-text-secondary)]">No email activity for this contact yet.</p>'
        return
      }

      this.listTarget.innerHTML = this.renderGroupedMessages(messages)
    } catch (e) {
      this.listTarget.innerHTML = '<p class="text-xs text-[var(--color-mc-text-secondary)]">Could not load email activity.</p>'
    }
  }

  renderGroupedMessages(messages) {
    const groups = {}
    messages.forEach(msg => {
      const date = new Date(msg.sent_at)
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
      if (!groups[key]) groups[key] = { label, messages: [] }
      groups[key].messages.push(msg)
    })

    const sortedKeys = Object.keys(groups).sort().reverse()

    return sortedKeys.map(key => {
      const group = groups[key]
      const msgHtml = group.messages.map(msg => this.renderMessage(msg)).join("")
      return `
        <div class="mb-4">
          <h4 class="text-xs font-semibold text-[var(--color-mc-text-secondary)] uppercase tracking-wide mb-2">${this.escapeHtml(group.label)}</h4>
          <div class="space-y-1">${msgHtml}</div>
        </div>
      `
    }).join("")
  }

  renderMessage(msg) {
    const date = new Date(msg.sent_at)
    const formattedDate = date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    const formattedTime = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })

    const isSent = msg.direction === "sent"
    const directionIcon = isSent
      ? '<svg class="w-3.5 h-3.5 text-[var(--color-mc-accent-terracotta)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"/></svg>'
      : '<svg class="w-3.5 h-3.5 text-[var(--color-mc-accent-dusty-blue)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>'

    const fontWeight = msg.is_read ? "font-normal" : "font-semibold"
    const contactLine = isSent
      ? `to ${this.escapeHtml(msg.to_name)}`
      : `from ${this.escapeHtml(msg.from_name)}`

    const threadBadge = msg.thread_count > 1
      ? `<span class="inline-flex items-center ml-1.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-[var(--color-mc-bg)] text-[var(--color-mc-text-secondary)]">
           <svg class="w-2.5 h-2.5 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/></svg>
           ${msg.thread_count}
         </span>`
      : ""

    const openDot = msg.open_count > 0
      ? '<span class="w-1.5 h-1.5 rounded-full bg-[var(--color-status-confirmed)] inline-block mr-0.5"></span>'
      : ''
    const clickDot = msg.click_count > 0
      ? '<span class="w-1.5 h-1.5 rounded-full bg-[var(--color-status-confirmed)] inline-block mr-0.5"></span>'
      : ''

    const trackingInfo = `<span class="text-[10px] text-[var(--color-mc-text-secondary)]">${openDot}Opens: ${msg.open_count} &middot; ${clickDot}Clicks: ${msg.click_count}</span>`

    const snippet = msg.snippet ? this.escapeHtml(this.decodeEntities(msg.snippet)).substring(0, 150) : ""
    const bodyText = msg.body_text ? this.escapeHtml(msg.body_text) : ""

    const userBadge = msg.user_name
      ? `<span class="text-[10px] text-[var(--color-mc-text-secondary)]">via ${this.escapeHtml(msg.user_name)}</span>`
      : ""

    const manualBadge = msg.sync_source === "manual"
      ? '<span class="px-1.5 py-0.5 text-[9px] font-medium rounded bg-[var(--color-mc-accent-dusty-blue)]/15 text-[var(--color-mc-accent-dusty-blue)]">Manual</span>'
      : ""

    const msgId = `email-body-${msg.id}`

    return `
      <div class="rounded-lg border border-[var(--color-mc-border)] hover:border-[var(--color-mc-accent-terracotta)]/30 transition-colors">
        <div class="flex items-start px-3 py-2.5 cursor-pointer" data-action="click->contact-email-activity#toggleBody" data-email-id="${msg.id}">
          <div class="flex-shrink-0 mt-0.5 mr-2">${directionIcon}</div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center flex-wrap gap-x-2">
              <span class="text-xs ${fontWeight} text-[var(--color-mc-text)] truncate">${this.escapeHtml(msg.subject || "(no subject)")}</span>
              ${threadBadge}
              ${manualBadge}
            </div>
            <div class="flex items-center gap-x-2 mt-0.5">
              <span class="text-[10px] text-[var(--color-mc-text-secondary)]">${contactLine}</span>
              ${userBadge}
              ${trackingInfo}
            </div>
            <p class="text-[10px] text-[var(--color-mc-text-secondary)] truncate mt-0.5">${snippet}</p>
          </div>
          <div class="flex-shrink-0 text-right ml-2">
            <span class="text-[10px] text-[var(--color-mc-text-secondary)] whitespace-nowrap">${formattedDate}</span>
            <br>
            <span class="text-[10px] text-[var(--color-mc-text-secondary)] whitespace-nowrap">${formattedTime}</span>
          </div>
        </div>
        <div id="${msgId}" class="hidden border-t border-[var(--color-mc-border)] px-3 py-2.5">
          <pre class="text-xs text-[var(--color-mc-text)] whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto">${bodyText || '<span class="text-[var(--color-mc-text-secondary)] italic">No body content</span>'}</pre>
          <div class="mt-2">
            <a href="/admin/email_inbox/${msg.id}" class="text-[10px] text-[var(--color-mc-accent-terracotta)] hover:underline">View in Inbox</a>
          </div>
        </div>
      </div>
    `
  }

  toggleBody(event) {
    const emailId = event.currentTarget.dataset.emailId
    const bodyEl = document.getElementById(`email-body-${emailId}`)
    if (bodyEl) bodyEl.classList.toggle("hidden")
  }

  openLogModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeLogModal() {
    this.modalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    // Reset form
    const form = this.modalTarget.querySelector("form")
    if (form) form.reset()
  }

  async submitLog(event) {
    event.preventDefault()
    const form = event.target
    const formData = new FormData(form)

    this.submitBtnTarget.disabled = true
    this.submitBtnTarget.textContent = "Saving..."

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const body = {
        contact_id: this.contactIdValue,
        direction: formData.get("direction"),
        subject: formData.get("subject"),
        body_text: formData.get("body_text"),
        sent_at: formData.get("sent_at")
      }

      const response = await fetch(this.logUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify(body)
      })

      const result = await response.json()

      if (result.success) {
        this.closeLogModal()
        this.loadEmails()
      } else {
        alert(result.message || "Failed to log email.")
      }
    } catch (e) {
      alert("An error occurred. Please try again.")
    } finally {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.textContent = "Log Email"
    }
  }

  decodeEntities(text) {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
